import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';
import { useCreateMicrostoreColumnGroupMutation, TotadminPermission } from 'data/__generated__';
import { TotemInput } from 'components/TotemInput';

export function CreateMicrostoreColumnGroupPopup({
    isOpen,
    setIsOpen,
    site,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    site: { _id: string; microstoreColumnGroups: { _id: string }[] };
}) {
    const [positionInSite, setPositionInSite] = useState<number>(site.microstoreColumnGroups.length);
    const [groupName, setGroupName] = useState<string>('');

    useEffect(() => {
        setPositionInSite(site.microstoreColumnGroups.length);
    }, [setPositionInSite, site]);

    const [createMicrostoreColumnGroup, { loading: creationLoading }] = useCreateMicrostoreColumnGroupMutation();

    async function handleSubmit() {
        if (!groupName) {
            return;
        }

        const { data } = await createMicrostoreColumnGroup({
            variables: {
                createMicrostoreColumnGroupInput: {
                    name: groupName,
                    positionInSite,
                    siteId: site._id,
                },
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la creation du template de colonne');
        }

        const {
            createMicrostoreColumnGroupMutation: { errors },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        }

        setIsOpen(false);
    }

    const positionInSiteOptions = Array(site.microstoreColumnGroups.length + 1)
        .fill(undefined)
        .map((_, index) => ({
            value: index,
            label: `${index + 1}`,
        }));

    return (
        <TotemPopup
            title="Ajouter un groupement de colonne"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            contentOverflow="visible"
        >
            <SubmitContainer>
                <TotemSelect<number>
                    label="Position du groupe"
                    placeholder="Sélectionner la position du groupe"
                    value={positionInSite}
                    options={positionInSiteOptions}
                    onChange={(value) => {
                        if (value !== null) {
                            setPositionInSite(value);
                        }
                    }}
                />
                <TotemInput
                    label="Nom du groupe"
                    placeholder="3e étage, cuisine etc..."
                    value={groupName}
                    onChange={(value) => setGroupName(value)}
                />
                <TotemPrimaryLoadingButton
                    loading={creationLoading}
                    onClick={handleSubmit}
                    disabled={!groupName}
                    useIcon={false}
                    label="Confirmer"
                    requiredPermissions={[TotadminPermission.MicrostoreColumnGroupsManage]}
                />
            </SubmitContainer>
        </TotemPopup>
    );
}

const SubmitContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 15px;

    > :not(:first-child) {
        margin-top: 15px;
    }
`;
